import type { OppwaFocusField, OppwaPreviewBridgeHandlers } from "./oppwaPreviewBridge";

export type { OppwaFocusField, OppwaPreviewBridgeHandlers };

const clampDigits = (value: string, maxLen: number) =>
  value.replace(/\D/g, "").slice(0, maxLen);

function parseExpiry(raw: string): { month: string; year: string } {
  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 4) {
    const month = digits.slice(0, 2);
    const yearPart = digits.slice(2, 6);
    const year =
      yearPart.length === 2 ? `20${yearPart}` : yearPart.length === 4 ? yearPart : "";
    return { month, year };
  }
  const parts = raw.split(/[/\-.]/).map((p) => p.trim());
  if (parts.length >= 2) {
    const month = clampDigits(parts[0], 2);
    let year = clampDigits(parts[1], 4);
    if (year.length === 2) year = `20${year}`;
    return { month, year };
  }
  return { month: "", year: "" };
}

function fieldFromTarget(target: EventTarget | null): OppwaFocusField | null {
  if (!(target instanceof Element)) return null;

  if (target instanceof HTMLIFrameElement) {
    const group = target.closest("[class*='wpwl-group-']");
    if (!group) return null;
    const cls = group.className.toLowerCase();
    if (cls.includes("cvv")) return "cvv";
    if (cls.includes("cardnumber")) return "number";
    if (cls.includes("expir")) return "expire";
    if (cls.includes("holder")) return "holder";
    return null;
  }

  const group = target.closest("[class*='wpwl-group-']");
  if (group) {
    const cls = group.className.toLowerCase();
    if (cls.includes("cvv")) return "cvv";
    if (cls.includes("cardnumber")) return "number";
    if (cls.includes("expir")) return "expire";
    if (cls.includes("holder")) return "holder";
  }

  if (!(target instanceof HTMLInputElement)) return null;
  const haystack = `${target.className} ${target.name}`.toLowerCase();
  if (haystack.includes("cvv")) return "cvv";
  if (haystack.includes("expir")) return "expire";
  if (haystack.includes("holder")) return "holder";
  if (haystack.includes("number")) return "number";
  return null;
}

function isIframeField(container: HTMLElement, selector: string): boolean {
  const group = container.querySelector(selector);
  if (!group) return false;
  return Boolean(group.querySelector("iframe")) && !group.querySelector("input");
}

/** One-way preview sync: OPPWA fields → React preview state. Event-driven only. */
export function attachOppwaCardPreviewSync(
  container: HTMLElement,
  handlers: OppwaPreviewBridgeHandlers
): () => void {
  let iframeNumberLen = 0;
  let iframeCvvLen = 0;
  let activeIframeField: OppwaFocusField | null = null;
  let lastFocus: OppwaFocusField | undefined;

  const setFocus = (field: OppwaFocusField) => {
    if (lastFocus === field) return;
    lastFocus = field ?? undefined;
    handlers.onFocus(field);
  };

  const syncFromPlainInputs = () => {
    const holderInput = container.querySelector(
      "input.wpwl-control-cardHolder, input[name='card.holder']"
    ) as HTMLInputElement | null;
    if (holderInput) handlers.onHolder(holderInput.value.toUpperCase());

    const expiryInput = container.querySelector(
      "input.wpwl-control-expiry, input[name='card.expiry']"
    ) as HTMLInputElement | null;
    if (expiryInput) {
      const { month, year } = parseExpiry(expiryInput.value);
      handlers.onMonth(month);
      handlers.onYear(year);
    }

    const numberInput = container.querySelector(
      "input.wpwl-control-cardNumber, input[name='card.number']"
    ) as HTMLInputElement | null;
    if (numberInput) handlers.onNumber(clampDigits(numberInput.value, 19));

    const cvvInput = container.querySelector(
      "input.wpwl-control-cvv, input[name='card.cvv']"
    ) as HTMLInputElement | null;
    if (cvvInput) handlers.onCvv(clampDigits(cvvInput.value, 4));
  };

  const applyIframeLengthPreview = () => {
    if (isIframeField(container, ".wpwl-group-cardNumber") && iframeNumberLen > 0) {
      handlers.onNumber("•".repeat(iframeNumberLen));
    }
    if (isIframeField(container, ".wpwl-group-cvv") && iframeCvvLen > 0) {
      handlers.onCvv("•".repeat(iframeCvvLen));
    }
  };

  const onInput = () => {
    syncFromPlainInputs();
    applyIframeLengthPreview();
  };

  const onFocusIn = (event: FocusEvent) => {
    const field = fieldFromTarget(event.target);
    if (field) {
      activeIframeField = field;
      setFocus(field);
    }
  };

  const onFocusOut = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (!container.contains(active)) {
        activeIframeField = null;
        setFocus(null);
        return;
      }
      const field = fieldFromTarget(active);
      activeIframeField = field;
      setFocus(field);
    }, 0);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!activeIframeField || !container.contains(document.activeElement)) return;
    const isDigit = event.key.length === 1 && /\d/.test(event.key);
    if (activeIframeField === "number" && isIframeField(container, ".wpwl-group-cardNumber")) {
      if (isDigit) iframeNumberLen = Math.min(19, iframeNumberLen + 1);
      if (event.key === "Backspace") iframeNumberLen = Math.max(0, iframeNumberLen - 1);
      applyIframeLengthPreview();
    }
    if (activeIframeField === "cvv" && isIframeField(container, ".wpwl-group-cvv")) {
      if (isDigit) iframeCvvLen = Math.min(4, iframeCvvLen + 1);
      if (event.key === "Backspace") iframeCvvLen = Math.max(0, iframeCvvLen - 1);
      applyIframeLengthPreview();
    }
  };

  container.addEventListener("input", onInput, true);
  container.addEventListener("change", onInput, true);
  container.addEventListener("focusin", onFocusIn, true);
  container.addEventListener("focusout", onFocusOut, true);
  window.addEventListener("keydown", onKeyDown, true);

  return () => {
    container.removeEventListener("input", onInput, true);
    container.removeEventListener("change", onInput, true);
    container.removeEventListener("focusin", onFocusIn, true);
    container.removeEventListener("focusout", onFocusOut, true);
    window.removeEventListener("keydown", onKeyDown, true);
  };
}
